import React, { useState, useEffect } from 'react';
import { Menu, X, Terminal, MessageCircle, Calendar, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import ChatModal from './ChatModal';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Skills', href: '#skills' },
    { label: 'Experience', href: '#experience' },
    { label: 'Projects', href: '#projects' },
    { label: 'GitHub', href: '#github' },
    { label: 'Certifications', href: '#certifications' },
    { label: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const openGoogleCalendar = () => {
    // Google Calendar appointment scheduling link
    const calendarUrl = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0vTFMJLK2vVXsWY-dKvJLqPMJN3KjZ0jGfJPyONzQjQ0tVXg7xPvYQy0qF_Lj4vJPQMR2JQKhN?gv=true';
    // Fallback: Create a new event with Murtaza
    const fallbackUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=Consultation%20with%20Murtaza%20Kanchwala&details=Data%20%26%20AI%20Consultation%20-%20Please%20describe%20your%20requirements&add=murtaza@kanchwala.in&sf=true&output=xml`;
    window.open(fallbackUrl, '_blank');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? theme === 'dark' 
            ? 'bg-[#1B3139]/95 backdrop-blur-md shadow-lg' 
            : 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-2">
            <Terminal className="h-6 w-6 text-[#FF3621]" />
            <span 
              className={`font-bold text-xl ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              MK
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className={`px-3 py-2 text-sm font-medium hover:text-[#FF3621] hover:bg-white/5 rounded-md transition-all duration-200 ${
                  theme === 'dark' ? 'text-white/80' : 'text-gray-700'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-300 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* Book a Call Button */}
            <Button
              onClick={openGoogleCalendar}
              className="bg-transparent border border-green-500 text-green-500 hover:bg-green-500 hover:text-white font-medium transition-all duration-300"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Book a Call
            </Button>

            <Button
              onClick={() => setIsChatOpen(true)}
              className="bg-transparent border border-[#FF3621] text-[#FF3621] hover:bg-[#FF3621] hover:text-white font-medium transition-all duration-300"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Ask Murtaza!
            </Button>
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-[#FF3621] text-white hover:bg-[#FF3621]/90 font-medium"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Get in Touch
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center space-x-2">
            {/* Theme Toggle - Mobile */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-300 ${
                theme === 'dark' 
                  ? 'text-white/60 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md transition-colors ${
                theme === 'dark' ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden border-t ${
          theme === 'dark' ? 'bg-[#1B3139] border-white/10' : 'bg-white border-gray-200'
        }`}>
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className={`block w-full text-left px-3 py-2 text-sm font-medium hover:text-[#FF3621] rounded-md transition-all duration-200 ${
                  theme === 'dark' ? 'text-white/80 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {item.label}
              </button>
            ))}
            <Button
              onClick={openGoogleCalendar}
              className="w-full mt-2 bg-transparent border border-green-500 text-green-500 hover:bg-green-500 hover:text-white font-medium"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Book a Call
            </Button>
            <Button
              onClick={() => {
                setIsChatOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="w-full mt-2 bg-transparent border border-[#FF3621] text-[#FF3621] hover:bg-[#FF3621] hover:text-white font-medium"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Ask Murtaza!
            </Button>
            <Button
              onClick={() => scrollToSection('#contact')}
              className="w-full mt-2 bg-[#FF3621] text-white hover:bg-[#FF3621]/90 font-medium"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Get in Touch
            </Button>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </header>
  );
};

export default Header;
